import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, Building2, Save, ShieldCheck, Info } from "lucide-react";

/**
 * Admin Profile tab — three cards:
 *   1. My Account     — edit name & phone (email & role are immutable)
 *   2. Change Password — current → new → confirm (uses /api/auth/change-password)
 *   3. Company Info    — name, CIN, GSTIN, PAN, address, email, phone, website,
 *                        default SAC, GST rate. Saves to db.settings.company_info.
 *
 * COMPANY INFO NOTE: Saved overrides are persisted but PDF/invoice templates
 * currently read from .env at API startup. A follow-up will wire runtime
 * lookup so saved values take effect for newly-generated invoices without
 * a redeploy. See banner in the card.
 */

const COMPANY_FIELDS = [
  { key: "name",        label: "Company Name",    placeholder: "Utkalic Innovations Private Limited" },
  { key: "cin",         label: "CIN",             placeholder: "U55101OD2025PTC048874" },
  { key: "gstin",       label: "GSTIN",           placeholder: "21AADCU8136B1ZS" },
  { key: "pan",         label: "PAN",             placeholder: "AADCU8136B" },
  { key: "email",       label: "Support Email",   placeholder: "support@safarsetu.com", type: "email" },
  { key: "phone",       label: "Support Phone",   placeholder: "+91 9930000510" },
  { key: "website",     label: "Website",         placeholder: "https://safarsetu.com" },
  { key: "default_sac", label: "Default SAC",     placeholder: "998552" },
  { key: "gst_rate",    label: "GST Rate (%)",    placeholder: "18", type: "number" },
];

export default function AdminProfileTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ me: false, password: false, company: false });
  const [profile, setProfile] = useState(null);
  const [meForm, setMeForm] = useState({ name: "", phone: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [company, setCompany] = useState({});
  const [companyEnvDefaults, setCompanyEnvDefaults] = useState({});
  const [hasDbOverride, setHasDbOverride] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/profile");
      setProfile(r.data);
      setMeForm({ name: r.data.me?.name || "", phone: r.data.me?.phone || "" });
      setCompany({ ...r.data.company });
      setCompanyEnvDefaults(r.data.company_env_defaults || {});
      setHasDbOverride(!!r.data.company_has_db_override);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveMe = async () => {
    setSaving(s => ({ ...s, me: true }));
    try {
      await api.put("/admin/profile/me", meForm);
      toast.success("Account details updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update account");
    } finally {
      setSaving(s => ({ ...s, me: false }));
    }
  };

  const handleChangePassword = async () => {
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setSaving(s => ({ ...s, password: true }));
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed. Use the new password on your next login.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(s => ({ ...s, password: false }));
    }
  };

  const handleSaveCompany = async () => {
    setSaving(s => ({ ...s, company: true }));
    try {
      const payload = { ...company };
      if (payload.gst_rate !== "" && payload.gst_rate != null) {
        payload.gst_rate = parseFloat(payload.gst_rate);
      }
      const r = await api.put("/admin/profile/company", payload);
      toast.success(`Company info saved (${r.data.saved_fields.length} fields)`);
      setHasDbOverride(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save company info");
    } finally {
      setSaving(s => ({ ...s, company: false }));
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#8A998E]">Loading profile…</div>;
  }
  if (!profile) {
    return <div className="text-center py-12 text-[#8A998E]">No profile data.</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-profile-tab-content">
      {/* My Account */}
      <section className="bg-white border border-[#E5E3DB] rounded-xl p-6" data-testid="profile-account-card">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-[#2C5F2D]" />
          <h3 className="font-['Outfit'] text-lg font-bold text-[#1A231C]">My Account</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Email (immutable)</Label>
            <Input value={profile.me?.email || ""} disabled className="bg-[#F3F2EE] rounded-xl mt-1 cursor-not-allowed" data-testid="profile-email-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Role</Label>
            <Input value={profile.me?.role || ""} disabled className="bg-[#F3F2EE] rounded-xl mt-1 cursor-not-allowed capitalize" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Name</Label>
            <Input value={meForm.name} onChange={e => setMeForm(f => ({ ...f, name: e.target.value }))} className="bg-white border-[#E5E3DB] rounded-xl mt-1" data-testid="profile-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Phone</Label>
            <Input value={meForm.phone} onChange={e => setMeForm(f => ({ ...f, phone: e.target.value }))} className="bg-white border-[#E5E3DB] rounded-xl mt-1" placeholder="+919999999999" data-testid="profile-phone-input" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleSaveMe} disabled={saving.me} className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" data-testid="profile-save-me-btn">
            <Save className="w-4 h-4 mr-2" />{saving.me ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </section>

      {/* Change Password */}
      <section className="bg-white border border-[#E5E3DB] rounded-xl p-6" data-testid="profile-password-card">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-5 h-5 text-[#2C5F2D]" />
          <h3 className="font-['Outfit'] text-lg font-bold text-[#1A231C]">Change Password</h3>
        </div>
        <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3 flex items-start gap-2 mb-4">
          <ShieldCheck className="w-4 h-4 text-[#065F46] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[#065F46]">
            Server-side seed no longer overwrites your password on restart. Once you set a new password here it stays.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Current password</Label>
            <Input type="password" autoComplete="current-password" value={pwd.current_password} onChange={e => setPwd(p => ({ ...p, current_password: e.target.value }))} className="bg-white border-[#E5E3DB] rounded-xl mt-1" data-testid="profile-current-pwd-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">New password</Label>
            <Input type="password" autoComplete="new-password" value={pwd.new_password} onChange={e => setPwd(p => ({ ...p, new_password: e.target.value }))} className="bg-white border-[#E5E3DB] rounded-xl mt-1" data-testid="profile-new-pwd-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Confirm new</Label>
            <Input type="password" autoComplete="new-password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} className="bg-white border-[#E5E3DB] rounded-xl mt-1" data-testid="profile-confirm-pwd-input" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleChangePassword} disabled={saving.password || !pwd.current_password || !pwd.new_password} className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" data-testid="profile-change-pwd-btn">
            <Lock className="w-4 h-4 mr-2" />{saving.password ? "Updating…" : "Update password"}
          </Button>
        </div>
      </section>

      {/* Company Info */}
      <section className="bg-white border border-[#E5E3DB] rounded-xl p-6" data-testid="profile-company-card">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-5 h-5 text-[#2C5F2D]" />
          <h3 className="font-['Outfit'] text-lg font-bold text-[#1A231C]">Company Information</h3>
          {hasDbOverride && <span className="ml-2 bg-[#DCFCE7] text-[#065F46] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">DB override active</span>}
        </div>
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3 flex items-start gap-2 mb-4">
          <Info className="w-4 h-4 text-[#B45309] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[#78350F]">
            Changes are saved to the database immediately and are auditable.
            Note: PDF invoice / agreement templates currently read these values
            from server <code className="bg-white px-1 rounded">.env</code> at startup. To apply your edits
            to newly-generated PDFs, also update <code className="bg-white px-1 rounded">/var/www/api/.env</code> and restart the API.
            A follow-up will wire runtime lookup.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMPANY_FIELDS.map(f => (
            <div key={f.key}>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">{f.label}</Label>
              <Input
                type={f.type || "text"}
                value={company[f.key] ?? ""}
                onChange={e => setCompany(c => ({ ...c, [f.key]: e.target.value }))}
                placeholder={companyEnvDefaults[f.key] ?? f.placeholder}
                className="bg-white border-[#E5E3DB] rounded-xl mt-1"
                data-testid={`profile-company-${f.key}-input`}
              />
              {companyEnvDefaults[f.key] != null && companyEnvDefaults[f.key] !== company[f.key] && (
                <p className="text-[11px] text-[#8A998E] mt-1">
                  .env default: <span className="font-mono">{String(companyEnvDefaults[f.key])}</span>
                </p>
              )}
            </div>
          ))}
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Registered Address</Label>
            <Input
              value={company.address ?? ""}
              onChange={e => setCompany(c => ({ ...c, address: e.target.value }))}
              placeholder={companyEnvDefaults.address || "Full registered address"}
              className="bg-white border-[#E5E3DB] rounded-xl mt-1"
              data-testid="profile-company-address-input"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={handleSaveCompany} disabled={saving.company} className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" data-testid="profile-save-company-btn">
            <Save className="w-4 h-4 mr-2" />{saving.company ? "Saving…" : "Save company info"}
          </Button>
        </div>
      </section>
    </div>
  );
}
